<template>
  <div class="markdown-editor-field">
    <!-- Header (optional) -->
    <div v-if="showHeader" class="markdown-editor-field__header">
      <div class="markdown-editor-field__title">
        <slot name="title">
          <span>{{ title }}</span>
        </slot>
      </div>

      <div class="markdown-editor-field__actions">
        <template v-if="autoSave && isEditing">
          <BaseButton
            v-if="hasConflict"
            isSmall
            isSquare
            @click.native="refreshAfterConflict"
            :disabled="savingComputed || disabled"
          >
            {{ $t('actions.refresh') }}
          </BaseButton>

          <BaseButton
            isSmall
            isUnderline
            isTransparent
            isSquare
            @click.native="exitEditMode"
            :disabled="savingComputed || disabled"
          >
            {{ $t('actions.done') }}
          </BaseButton>

          <div
            class="markdown-editor-field__autosave"
            :class="autosaveStatusClass"
            v-tooltip="{ content: autosaveStatusLabel, placement: 'top' }"
          >
            <BaseIcon :name="autosaveIconName" />
          </div>
        </template>

        <!-- Standard actions when auto-save controls are not shown -->
        <slot
          v-else
          name="actions"
          :isEditing="isEditing"
          :saving="savingComputed"
          :disabled="disabled"
          :startEdit="startEdit"
          :cancel="cancelEdit"
          :save="save"
        >
          <BaseButton
            v-if="!isEditing"
            isSmall
            isUnderline
            isTransparent
            isSquare
            @click.native="startEdit"
            :disabled="savingComputed || disabled"
          >
            {{ savingComputed ? $t('common.saving') : $t('actions.edit') }}
          </BaseButton>

          <template v-else>
            <BaseButton
              isSmall
              isUnderline
              isTransparent
              isSquare
              @click.native="cancelEdit"
              :disabled="savingComputed || disabled"
            >
              {{ $t('actions.cancel') }}
            </BaseButton>

            <BaseButton isSmall isSquare @click.native="save" :disabled="savingComputed || disabled">
              {{ savingComputed ? $t('common.saving') : $t('actions.save') }}
            </BaseButton>
          </template>
        </slot>
      </div>
    </div>

    <!-- PREVIEW MODE -->
    <div v-if="!isEditing" class="markdown-editor-field__view">
      <Viewer v-if="currentValue" ref="viewer" :initialValue="currentValue" @load="onViewerLoad" />
      <div v-else class="markdown-editor-field__empty">
        <slot name="empty">
          {{ $t('common.empty.no-content') }}
        </slot>
      </div>
    </div>

    <!-- EDIT MODE -->
    <div v-else class="markdown-editor-field__edit" ref="editWrap" @focusin="onEditorFocusIn">
      <Editor
        ref="editor"
        :initial-value="draft"
        initial-edit-type="wysiwyg"
        :height="height"
        :options="mergedEditorOptions"
        @change="onEditorChange"
      />
    </div>
  </div>
</template>

<script>
import BaseButton from '@/components/base/BaseButton.vue';
import { Editor, Viewer } from '@toast-ui/vue-editor';
import '@toast-ui/editor/dist/toastui-editor.css';
import '@toast-ui/editor/dist/toastui-editor-viewer.css';
import '@toast-ui/editor/dist/i18n/fr-fr';
import { mapGetters } from 'vuex';
import { getLanguageCode } from '@/util/i18n/get-language-code';
import BaseIcon from '@/components/base/BaseIcon.vue';

export default {
  name: 'MarkdownEditor',
  components: {
    BaseIcon,
    BaseButton,
    Editor,
    Viewer,
  },

  props: {
    // v-model compatible
    value: { type: String, default: '' },

    // UI
    title: { type: String, default: '' },
    showHeader: { type: Boolean, default: true },
    height: { type: String, default: '320px' },

    // State control
    disabled: { type: Boolean, default: false },

    // If parent controls saving state, pass boolean here. If null, component manages internally.
    saving: { type: Boolean, default: null },

    // Editor options (parent can override/extend)
    editorOptions: { type: Object, default: () => ({}) },

    // Checkbox behavior in preview
    enableTaskCheckboxToggle: { type: Boolean, default: true },

    // Auto-save
    autoSave: { type: Boolean, default: false },
    autoSaveDelay: { type: Number, default: 1000 },

    // Hooks for refreshing content before edit and on conflict
    beforeEdit: { type: Function, default: null },
    onConflictRefresh: { type: Function, default: null },
    idleRefreshDelay: { type: Number, default: 30000 },
  },

  data() {
    return {
      isEditing: false,
      draft: this.value || '',
      internalSaving: false,
      checkboxHandler: null,

      autosaveTimer: null,
      autosaveState: 'idle', // idle | dirty | saving | success | error
      lastSavedValue: this.value || '',

      refreshBeforeEditInFlight: false,
      idleRefreshTimer: null,
      lastEditorInteractionAt: 0,

      hasConflict: false,
    };
  },

  computed: {
    ...mapGetters('auth', ['currentUser']),
    currentValue() {
      return this.value || '';
    },

    savingComputed() {
      return this.saving === null ? this.internalSaving : this.saving;
    },

    mergedEditorOptions() {
      // Defaults kept generic; parent can pass placeholder/toolbarItems/etc.
      const defaults = {
        hideModeSwitch: true,
        autofocus: false,
        usageStatistics: false,
        language: this.toastLocale,
        toolbarItems: [
          ['heading', 'bold', 'italic'],
          ['ul', 'ol', 'task'],
          ['quote', 'link', 'image'],
        ],
      };
      return { ...defaults, ...this.editorOptions };
    },

    toastLocale() {
      const locale =
        this.currentUser && this.currentUser.locale ? this.currentUser.locale : navigator.language || 'en-US';

      return getLanguageCode(locale);
    },

    autosaveStatusClass() {
      return {
        'is-idle': this.autosaveState === 'idle',
        'is-dirty': this.autosaveState === 'dirty',
        'is-saving': this.autosaveState === 'saving',
        'is-success': this.autosaveState === 'success',
        'is-error': this.autosaveState === 'error',
      };
    },

    autosaveIconName() {
      return this.autosaveState === 'error' ? 'x' : 'check';
    },

    autosaveStatusLabel() {
      const labels = {
        dirty: this.$t('common.unsaved'),
        saving: this.$t('common.saving'),
        success: this.$t('common.saved'),
        error: this.$t('common.error'),
      };

      return labels[this.autosaveState] || '';
    },
  },

  watch: {
    value(newVal) {
      const normalized = newVal || '';

      if (!this.isEditing) {
        this.draft = normalized;
        this.lastSavedValue = normalized;
        return;
      }

      if (this.autosaveState === 'saving' && normalized === this.draft) {
        this.lastSavedValue = normalized;
        this.autosaveState = 'success';
      }
    },
  },

  methods: {
    async startEdit() {
      if (this.disabled || this.savingComputed) return;

      if (typeof this.beforeEdit === 'function') {
        await this.beforeEdit();
      }

      this.isEditing = true;
      this.draft = this.currentValue;
      this.lastSavedValue = this.currentValue;
      this.autosaveState = 'idle';
      this.lastEditorInteractionAt = Date.now();
      this.startIdleRefreshWatcher();

      this.$nextTick(() => {
        const el = this.$refs.editWrap;
        if (el) el.scrollIntoView({ behavior: 'auto', block: 'end' });

        if (this.$refs.editor) {
          this.$refs.editor.invoke('setMarkdown', this.draft || '', false);
          this.$refs.editor.invoke('focus');
        }
      });
    },

    closeEditMode() {
      this.stopIdleRefreshWatcher();
      this.clearAutosaveTimer();
      this.isEditing = false;
      this.autosaveState = 'idle';
    },

    cancelEdit() {
      this.draft = this.currentValue;
      this.closeEditMode();
    },

    async exitEditMode() {
      if (this.disabled || this.savingComputed) return;

      this.clearAutosaveTimer();

      const markdown = this.getEditorMarkdown();
      const hasUnsavedChanges = markdown !== this.lastSavedValue;

      if (!hasUnsavedChanges) {
        this.closeEditMode();
        return;
      }

      try {
        await this.performSave(markdown, { closeAfterSave: true });
      } catch (e) {
        console.error('Save before exit failed', e);
      }
    },

    onEditorChange() {
      this.lastEditorInteractionAt = Date.now();

      if (!this.autoSave || !this.isEditing || this.disabled) return;

      this.draft = this.getEditorMarkdown();
      this.scheduleAutosave();
    },

    async save() {
      if (this.disabled || this.savingComputed) return;

      const markdown = this.getEditorMarkdown();
      await this.performSave(markdown, { closeAfterSave: !this.autoSave });
    },

    async performSave(markdown, { closeAfterSave = false } = {}) {
      const manageSavingInternally = this.saving === null;

      try {
        if (manageSavingInternally) this.internalSaving = true;
        this.autosaveState = 'saving';
        this.hasConflict = false;

        await this.emitSave(markdown);

        this.$emit('input', markdown);
        this.$emit('change', markdown);

        this.draft = markdown;
        this.lastSavedValue = markdown;
        this.autosaveState = 'success';

        if (this.autoSave) {
          clearTimeout(this._autosaveSuccessTimer);
          this._autosaveSuccessTimer = setTimeout(() => {
            if (this.autosaveState === 'success' && this.draft === this.lastSavedValue) {
              this.autosaveState = 'idle';
            }
          }, 5000);
        }

        if (closeAfterSave) {
          this.closeEditMode();
        }
      } catch (e) {
        if (e && e.response && e.response.status === 409) {
          this.hasConflict = true;
        }

        this.autosaveState = 'error';
        throw e;
      } finally {
        if (manageSavingInternally) this.internalSaving = false;
      }
    },

    emitSave(markdown) {
      const handler = this.$listeners.save;
      if (typeof handler === 'function') {
        const res = handler(markdown);
        if (res && typeof res.then === 'function') return res;
      }
      this.$emit('save', markdown);
      return Promise.resolve();
    },

    getEditorMarkdown() {
      return this.$refs.editor ? this.$refs.editor.invoke('getMarkdown') : this.draft;
    },

    scheduleAutosave() {
      if (!this.autoSave || this.disabled || !this.isEditing) return;

      if (this.draft === this.lastSavedValue) {
        this.autosaveState = 'idle';
        this.clearAutosaveTimer();
        return;
      }

      this.autosaveState = 'dirty';
      this.clearAutosaveTimer();

      this.autosaveTimer = setTimeout(async () => {
        if (this.draft === this.lastSavedValue) {
          this.autosaveState = 'idle';
          return;
        }

        try {
          await this.performSave(this.draft, { closeAfterSave: false });
        } catch (e) {
          console.error('Auto-save failed', e);
        }
      }, this.autoSaveDelay);
    },

    clearAutosaveTimer() {
      if (this.autosaveTimer) {
        clearTimeout(this.autosaveTimer);
        this.autosaveTimer = null;
      }
    },

    onViewerLoad() {
      if (!this.enableTaskCheckboxToggle) return;
      if (!this.$refs.viewer) return;

      const viewerRoot = this.$refs.viewer.getRootElement();

      this.checkboxHandler = (event) => {
        if (this.savingComputed || this.disabled) return;

        const listItem = event.target.closest('.task-list-item');

        // Strict hit-target: only LI itself (gutter), not children
        if (listItem && event.target === listItem) {
          event.preventDefault();
          event.stopPropagation();

          const currentlyChecked = listItem.classList.contains('checked');
          const allTaskItems = Array.from(viewerRoot.querySelectorAll('.task-list-item'));
          const index = allTaskItems.indexOf(listItem);

          if (index !== -1) {
            this.handleManualCheckboxToggle(index, !currentlyChecked, listItem);
          }
        }
      };

      viewerRoot.addEventListener('mousedown', this.checkboxHandler, true);
    },

    async handleManualCheckboxToggle(index, isChecked, element) {
      const originalMarkdown = this.currentValue || '';

      // 1) Optimistic UI update
      if (isChecked) {
        element.classList.add('checked');
        element.setAttribute('data-task-checked', '');
        element.setAttribute('aria-checked', 'true');
      } else {
        element.classList.remove('checked');
        element.removeAttribute('data-task-checked');
        element.setAttribute('aria-checked', 'false');
      }

      // 2) Regex replacement
      let count = 0;
      const taskRegex = /^(\s*(?:[-*+]|\d+\.)\s+\[)([ xX])(\])/gm;

      const updatedMarkdown = originalMarkdown.replace(taskRegex, (full, left, mark, right) => {
        if (count === index) {
          count++;
          return `${left}${isChecked ? 'x' : ' '}${right}`;
        }
        count++;
        return full;
      });

      const manageSavingInternally = this.saving === null;

      try {
        if (manageSavingInternally) this.internalSaving = true;

        await this.emitSave(updatedMarkdown);

        this.$emit('input', updatedMarkdown);
        this.$emit('change', updatedMarkdown);

        // 4) Keep Viewer instance in sync
        if (this.$refs.viewer) {
          this.$refs.viewer.invoke('setMarkdown', updatedMarkdown);
        }
      } catch (e) {
        // 5) Rollback on failure
        // eslint-disable-next-line no-console
        console.error('Checkbox save failed', e);

        if (isChecked) {
          element.classList.remove('checked');
          element.removeAttribute('data-task-checked');
          element.setAttribute('aria-checked', 'false');
        } else {
          element.classList.add('checked');
          element.setAttribute('data-task-checked', '');
          element.setAttribute('aria-checked', 'true');
        }
      } finally {
        if (manageSavingInternally) this.internalSaving = false;
      }
    },

    clampToastUiPopup() {
      this.$nextTick(() => {
        const popup = document.querySelector('.toastui-editor-popup[style*="display: block"]');
        if (!popup) return;

        const margin = 8;
        const vw = window.innerWidth;
        const vh = window.innerHeight;

        // Sidebar safe zone
        const sidebar = document.querySelector('.sidebar__menu');
        const sidebarRect = sidebar ? sidebar.getBoundingClientRect() : null;
        const safeLeft = sidebarRect ? sidebarRect.right + margin : margin;

        // Current popup position in viewport coords
        const rect = popup.getBoundingClientRect();

        // Compute desired viewport position (clamped)
        let desiredLeft = rect.left;
        let desiredTop = rect.top;

        // Clamp horizontally against sidebar + right edge
        if (rect.left < safeLeft) desiredLeft = safeLeft;
        if (rect.right > vw - margin) desiredLeft = vw - rect.width - margin;

        // Clamp vertically (optional)
        if (rect.bottom > vh - margin) desiredTop = vh - rect.height - margin;
        if (desiredTop < margin) desiredTop = margin;

        // Convert viewport adjustment -> element's own inline left/top coordinates
        const deltaX = desiredLeft - rect.left;
        const deltaY = desiredTop - rect.top;
        if (deltaX === 0 && deltaY === 0) return;

        const currentLeft = parseFloat(popup.style.left || '0') || 0;
        const currentTop = parseFloat(popup.style.top || '0') || 0;

        popup.style.left = `${currentLeft + deltaX}px`;
        popup.style.top = `${currentTop + deltaY}px`;
      });
    },

    installToastUiPopupClamp() {
      // Only react to toolbar interactions (popups open from there)
      this._popupClampHandler = (e) => {
        const isToolbarClick =
          e.target.closest('.toastui-editor-toolbar') || e.target.closest('.toastui-editor-dropdown-toolbar');

        if (!isToolbarClick) return;

        requestAnimationFrame(() => this.clampToastUiPopup());
      };

      this.$el.addEventListener('click', this._popupClampHandler, true);
      window.addEventListener('resize', this.clampToastUiPopup);
    },

    uninstallToastUiPopupClamp() {
      if (this._popupClampHandler) {
        this.$el.removeEventListener('click', this._popupClampHandler, true);
        this._popupClampHandler = null;
      }
      window.removeEventListener('resize', this.clampToastUiPopup);
    },

    async onEditorFocusIn() {
      if (!this.isEditing || this.disabled || this.savingComputed) return;
      if (typeof this.beforeEdit !== 'function') return;
      if (this.refreshBeforeEditInFlight) return;

      this.refreshBeforeEditInFlight = true;

      try {
        await this.refreshEditorContent(this.beforeEdit, {
          onlyIfClean: true,
          focus: false,
          resetConflict: false,
        });
      } catch (e) {
        console.error('Failed to refresh before continuing edit', e);
      } finally {
        this.refreshBeforeEditInFlight = false;
      }
    },

    startIdleRefreshWatcher() {
      this.stopIdleRefreshWatcher();

      this.idleRefreshTimer = setInterval(() => {
        this.maybeRefreshWhileIdle();
      }, 5000);
    },

    stopIdleRefreshWatcher() {
      if (this.idleRefreshTimer) {
        clearInterval(this.idleRefreshTimer);
        this.idleRefreshTimer = null;
      }
    },

    async maybeRefreshWhileIdle() {
      if (!this.isEditing || this.disabled || this.savingComputed) return;
      if (typeof this.beforeEdit !== 'function') return;
      if (this.refreshBeforeEditInFlight) return;

      const idleFor = Date.now() - this.lastEditorInteractionAt;
      if (idleFor < this.idleRefreshDelay) return;

      this.refreshBeforeEditInFlight = true;

      try {
        await this.refreshEditorContent(this.beforeEdit, {
          onlyIfClean: true,
          focus: false,
          resetConflict: false,
        });
      } catch (e) {
        console.error('Idle refresh failed', e);
      } finally {
        this.refreshBeforeEditInFlight = false;
      }
    },

    async refreshAfterConflict() {
      if (this.disabled || this.savingComputed) return;
      if (typeof this.onConflictRefresh !== 'function') return;

      try {
        await this.refreshEditorContent(this.onConflictRefresh, {
          onlyIfClean: false,
          focus: true,
          resetConflict: true,
        });
      } catch (e) {
        console.error('Conflict refresh failed', e);
      }
    },

    async refreshEditorContent(refreshFn, { focus = false, resetConflict = false, onlyIfClean = true } = {}) {
      if (typeof refreshFn !== 'function') return false;

      if (onlyIfClean && this.draft !== this.lastSavedValue) return false;

      await refreshFn();

      // Re-check after await, in case state changed while request was in flight
      if (!this.isEditing) return false;
      if (onlyIfClean && this.draft !== this.lastSavedValue) return false;

      const refreshedValue = this.value || '';

      if (resetConflict) {
        this.hasConflict = false;
        this.autosaveState = 'idle';
      }

      if (refreshedValue === this.draft) {
        if (focus) {
          this.$nextTick(() => {
            if (this.$refs.editor) {
              this.$refs.editor.invoke('focus');
            }
          });
        }
        return true;
      }

      this.draft = refreshedValue;
      this.lastSavedValue = refreshedValue;

      this.$nextTick(() => {
        if (this.$refs.editor) {
          this.$refs.editor.invoke('setMarkdown', refreshedValue, false);
          if (focus) {
            this.$refs.editor.invoke('focus');
          }
        }
      });

      return true;
    },
  },

  mounted() {
    this.installToastUiPopupClamp();
  },

  beforeDestroy() {
    this.stopIdleRefreshWatcher();
    this.uninstallToastUiPopupClamp();
    this.clearAutosaveTimer();
    clearTimeout(this._autosaveSuccessTimer);

    if (this.$refs.viewer && this.checkboxHandler) {
      const viewerRoot = this.$refs.viewer.getRootElement();
      viewerRoot.removeEventListener('mousedown', this.checkboxHandler, true);
    }
  },
};
</script>

<style lang="scss" scoped>
.markdown-editor-field {
  &__edit {
    scroll-margin-top: 12px;
    scroll-margin-bottom: 12px;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-bottom: 16px;
    gap: 8px;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__empty {
    color: $color-neutral;
  }

  &__autosave {
    height: 18px;
    width: 18px;
    border-radius: 999px;
    display: flex;
    justify-content: center;
    align-items: center;
    transition: background-color 0.2s ease, color 0.2s ease;

    .base-icon {
      height: 12px;
      width: 12px;
    }

    &.is-idle {
      background-color: $color-grey-pale;
      color: $color-white;
    }

    &.is-dirty,
    &.is-saving {
      background-color: $color-white;
      color: $color-grey-pale;
      border: 1px solid $color-grey-pale;
    }

    &.is-success {
      background-color: $color-success;
      color: $color-white;
    }

    &.is-error {
      background-color: $color-error;
      color: $color-white;
    }
  }
}
</style>
